import { useStorageApi, useUnsafeAppContext } from '@nx/state';
import { Objects, isNotNullish } from '@nx/stdlib';
import { useAppDispatch, useAppSelector } from '@query/redux/store';
import {
  trackSavedCypherCreated as trackSaveCypherQueryCreated,
  trackNewSavedCypherFolderCreated as trackSavedCypherFolderCreated,
  trackSavedCypherOverwrite,
} from '@query/services/analytics';
import {
  useBatchUpdateSavedCypherItemsMutation,
  useCommonBatchCreateSavedCypherItemsMutation,
  useCommonBatchDeleteSavedCypherItemsMutation,
  useCommonGetFlatSavedCypherTreeQuery,
  useCommonPatchSavedCypherTreeMutation,
} from '@query/services/api';
import type {
  CreateSavedCypherFolderPayload,
  CreateSavedCypherQueryPayload,
  FlatSavedCypherFolder,
  FlatSavedCypherQuery,
  FlatTreeItems,
  ImportedBrowserDocument,
  ImportedBrowserFolder,
  SavedCypherLoadingState,
  UpdateSavedCypherFolderPayload,
  UpdateSavedCypherQueryPayload,
} from '@query/types/saved-cypher';
import {
  NEW_FOLDER_PREFIX,
  isChild,
  isCreateSavedCypherQueryPayload,
  isSavedCypherFolder,
  isSavedCypherQuery,
  isUpdateSavedCypherQueryPayload,
} from '@query/types/saved-cypher';
import { getItemFromLocalStorage, setItemInLocalStorage } from '@query/utils/localstorage-utils';
import {
  MAXIMUM_SAVED_CYPHER_ITEMS,
  ROOT_ITEM_ID,
  buildTree,
  findDuplicateInFolder,
  findItemDeep,
  flattenTree,
  getNameForSavedCypher,
  getNewFolderName,
  mergeTrees,
} from '@query/utils/saved-cypher-utils';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice, nanoid } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { openSidebarTab } from './sidebar-slice';
import type { RootState } from './store';

export interface SavedCypherState {
  flattenedSavedCypherTree: FlatTreeItems;
  latestFolderSavedIn: string | null;
  folderPendingRename: string | null;
  savedCypherTreeMigratedV2: boolean | null;
}

export const SAVED_CYPHER_PERSISTED_KEYS = ['flattenedSavedCypherTree', 'savedCypherTreeMigratedV2'];

export const FOLDERS_COLLAPSED_KEY = 'foldersCollapsed';

const createSavedCypherQuery = createAsyncThunk(
  'savedCypher/createQuery',
  ({ name, query, parentId }: CreateSavedCypherQueryPayload) => {
    trackSaveCypherQueryCreated();
    return {
      id: nanoid(),
      name,
      query,
      parentId: parentId ?? ROOT_ITEM_ID,
    };
  },
);

const createSavedCypherFolder = createAsyncThunk(
  'savedCypher/createFolder',
  ({ parentId, name }: CreateSavedCypherFolderPayload) => {
    trackSavedCypherFolderCreated();
    return {
      id: nanoid(),
      parentId: parentId ?? ROOT_ITEM_ID,
      collapsed: true,
      children: [],
      name,
    };
  },
);

const selectFlatSavedCypherTree = (state: RootState) => state.savedCypher.flattenedSavedCypherTree;

const selectSavedCypherTreeMigrated = (state: RootState) => state.savedCypher.savedCypherTreeMigratedV2 ?? false;

export const selectLatestFolderSavedIn = (state: RootState) => state.savedCypher.latestFolderSavedIn;

export const selectFolderPendingRename = (state: RootState) => state.savedCypher.folderPendingRename;

const initialState: SavedCypherState = {
  flattenedSavedCypherTree: [],
  latestFolderSavedIn: null,
  folderPendingRename: null,
  savedCypherTreeMigratedV2: null,
};

const savedCypher = createSlice({
  name: 'savedCypher',
  initialState,
  reducers: {
    updateTree: (state, action: PayloadAction<FlatTreeItems>) => {
      state.flattenedSavedCypherTree = action.payload;
    },
    removeItems: (state, action: PayloadAction<string[]>) => {
      state.flattenedSavedCypherTree = state.flattenedSavedCypherTree.filter(
        (savedCypherItem) => !action.payload.includes(savedCypherItem.id),
      );
    },
    updateItem: (state, action: PayloadAction<UpdateSavedCypherQueryPayload | UpdateSavedCypherFolderPayload>) => {
      const { id } = action.payload;
      const itemIdx = state.flattenedSavedCypherTree.findIndex((t) => t.id === id);
      const item = state.flattenedSavedCypherTree[itemIdx];

      if (isUpdateSavedCypherQueryPayload(action.payload)) {
        const { newValues } = action.payload;
        if (item !== undefined && isSavedCypherQuery(item)) {
          state.flattenedSavedCypherTree[itemIdx] = { ...item, ...newValues };
        }
      } else {
        const { name } = action.payload;
        if (item !== undefined && isSavedCypherFolder(item)) {
          item.name = name;
        }
        state.folderPendingRename = null;
      }
    },
    setLatestFolderSavedIn: (state, action: PayloadAction<string | null>) => {
      state.latestFolderSavedIn = action.payload;
    },
    importTree: (state, action: PayloadAction<FlatTreeItems>) => {
      const importTree = buildTree(action.payload);
      const currentTree = buildTree(state.flattenedSavedCypherTree);
      const mergedTree = mergeTrees(currentTree, importTree);
      state.flattenedSavedCypherTree = flattenTree(mergedTree);
    },
    setFolderPendingRename: (state, action: PayloadAction<{ id: string | null }>) => {
      state.folderPendingRename = action.payload.id;
    },
    setMigrated: (state, action: PayloadAction<boolean>) => {
      state.savedCypherTreeMigratedV2 = action.payload;
    },
  },
  extraReducers(builder) {
    builder.addCase(createSavedCypherQuery.fulfilled, (state, action) => {
      const parentItem = state.flattenedSavedCypherTree.find((item) => item.id === action.payload.parentId);
      const depth = parentItem !== undefined ? parentItem.depth + 1 : 0;
      const order = state.flattenedSavedCypherTree.length;

      state.flattenedSavedCypherTree.push({
        ...action.payload,
        depth,
        order,
      });
      state.latestFolderSavedIn = parentItem !== undefined ? parentItem.id : null;
    });
    builder.addCase(createSavedCypherFolder.fulfilled, (state, action) => {
      const folders = state.flattenedSavedCypherTree.filter((item) => isSavedCypherFolder(item));
      const parentItem = folders.find((item) => item.id === action.payload.parentId);
      const depth = parentItem !== undefined ? parentItem.depth + 1 : 0;
      const order = state.flattenedSavedCypherTree.length;

      if (action.payload.name !== null) {
        if (
          folders.filter((folder) => folder.depth === 0).find((folder) => folder.name === action.payload.name) !==
          undefined
        ) {
          return;
        }
        const { name, ...rest } = action.payload;
        const newFolder: FlatSavedCypherFolder = {
          ...rest,
          name,
          depth,
          order,
        };
        state.flattenedSavedCypherTree.push(newFolder);
        return;
      }

      const name = getNewFolderName(folders, action.payload.name);
      const newFolder: FlatSavedCypherFolder = {
        ...action.payload,
        name,
        depth,
        order,
      };

      state.flattenedSavedCypherTree.push(newFolder);

      if (newFolder.name.startsWith(NEW_FOLDER_PREFIX)) {
        state.folderPendingRename = newFolder.id;
      }
    });
  },
});

const {
  removeItems: removeSavedCypherItems,
  updateItem: updateSavedCypherItem,
  importTree: importSavedCypherItems,
  updateTree: updateSavedCypherItems,
  setLatestFolderSavedIn: setSavedCypherLatestFolderSavedIn,
  setMigrated: setSavedCypherTreeMigrated,
} = savedCypher.actions;

export const { setFolderPendingRename } = savedCypher.actions;

export function useLocalStorage<T>(key: string, initialValue: T): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [state, setState] = useState(() => getItemFromLocalStorage(key, initialValue));

  const setLocalStorageState: React.Dispatch<React.SetStateAction<T>> = (newState) => {
    setItemInLocalStorage(key, newState);
    setState(newState);
  };

  return [state, setLocalStorageState];
}

// Pitfall alert: this hook is tightly coupled to RTK Query and the saved cypher tree structure.
// When the state of the saved cypher tree changes in redux, this hook will re-run and any subscribers
// will re-render. This is intended, but be aware that it may cause performance issues if used in
// components that render multiple times.
export const useFlatSavedCypherTree = (): {
  savedCypherTreeId: string;
  flatSavedCypherTree: FlatTreeItems;
  flatSavedCypherTreeState: SavedCypherLoadingState;
} => {
  const storageApiEnabled = useStorageApi('query:saved-cypher-shared-storage');
  const { activeProjectId } = useUnsafeAppContext();
  const skip = activeProjectId === null || !storageApiEnabled;
  const localStorageFlatSavedCypherTree = useAppSelector(selectFlatSavedCypherTree);
  const { savedCypherTreeId, flatSavedCypherTree, isLoading, isError, isSuccess } =
    useCommonGetFlatSavedCypherTreeQuery({
      skip,
    });

  return useMemo(() => {
    return {
      savedCypherTreeId: !storageApiEnabled ? '' : savedCypherTreeId,
      flatSavedCypherTree: !storageApiEnabled ? localStorageFlatSavedCypherTree : flatSavedCypherTree,
      flatSavedCypherTreeState: {
        isLoading: storageApiEnabled ? isLoading : false,
        isError: storageApiEnabled ? isError : false,
        isSuccess: storageApiEnabled ? isSuccess : true,
      },
    };
  }, [
    flatSavedCypherTree,
    isError,
    isLoading,
    isSuccess,
    localStorageFlatSavedCypherTree,
    savedCypherTreeId,
    storageApiEnabled,
  ]);
};

export const useSelectLatestFolderSavedIn = (flatSavedCypherTree: FlatTreeItems) => {
  const latestFolderSavedIn = useAppSelector(selectLatestFolderSavedIn);

  if (latestFolderSavedIn === null) {
    return null;
  }

  const item = findItemDeep(flatSavedCypherTree, latestFolderSavedIn);
  if (item === undefined || isSavedCypherQuery(item)) {
    return null;
  }

  return item;
};

const usePruneFoldersCollapsed = () => {
  const [foldersCollapsed, setFoldersCollapsed] = useLocalStorage<Record<string, boolean>>(FOLDERS_COLLAPSED_KEY, {});
  return (ids: string[]) => {
    setFoldersCollapsed({ ...Objects.removeKeys<Record<string, boolean>>(foldersCollapsed, ids) });
  };
};

export const useDeleteSavedCypherItems = () => {
  const dispatch = useAppDispatch();
  const storageApiEnabled = useStorageApi('query:saved-cypher-shared-storage');
  const clearFoldersCollapsed = usePruneFoldersCollapsed();
  const [batchDeleteSavedCypherItemsMutation, batchDeleteSavedCypherItemsMutationState] =
    useCommonBatchDeleteSavedCypherItemsMutation();

  const [deleteSavedCypherItemsInternalState, setDeleteSavedCypherItemsInternalState] = useState<
    Pick<SavedCypherLoadingState, 'isLoading' | 'isError'>
  >({
    isLoading: false,
    isError: false,
  });

  useEffect(() => {
    if (storageApiEnabled) {
      setDeleteSavedCypherItemsInternalState({
        isLoading: batchDeleteSavedCypherItemsMutationState.isLoading,
        isError: batchDeleteSavedCypherItemsMutationState.isError,
      });
    }
  }, [
    storageApiEnabled,
    batchDeleteSavedCypherItemsMutationState.isLoading,
    batchDeleteSavedCypherItemsMutationState.isError,
  ]);

  const deleteSavedCypherItemsInternal = useCallback(
    async (savedCypherTreeId: string, ids: string[]) => {
      setDeleteSavedCypherItemsInternalState({ isLoading: true, isError: false });

      if (storageApiEnabled) {
        await batchDeleteSavedCypherItemsMutation({ itemIds: ids, savedCypherTreeId });
        clearFoldersCollapsed(ids);
        return ids;
      }

      try {
        dispatch(removeSavedCypherItems(ids));
        clearFoldersCollapsed(ids);
        setDeleteSavedCypherItemsInternalState({ isLoading: false, isError: false });
        return ids;
      } catch (error) {
        setDeleteSavedCypherItemsInternalState({ isLoading: false, isError: true });
        throw error;
      }
    },
    [clearFoldersCollapsed, batchDeleteSavedCypherItemsMutation, dispatch, storageApiEnabled],
  );

  return [deleteSavedCypherItemsInternal, deleteSavedCypherItemsInternalState] as const;
};

export const useCreateSavedCypherItem = () => {
  const dispatch = useAppDispatch();
  const storageApiEnabled = useStorageApi('query:saved-cypher-shared-storage');
  const [batchCreateSavedCypherItemsMutation, batchCreateSavedCypherItemsMutationState] =
    useCommonBatchCreateSavedCypherItemsMutation();

  const [createSavedCypherItemInternalState, setCreateSavedCypherItemInternalState] = useState<
    Pick<SavedCypherLoadingState, 'isLoading'>
  >({
    isLoading: false,
  });

  useEffect(() => {
    if (storageApiEnabled) {
      setCreateSavedCypherItemInternalState({
        isLoading: batchCreateSavedCypherItemsMutationState.isLoading,
      });
    }
  }, [storageApiEnabled, batchCreateSavedCypherItemsMutationState.isLoading]);

  const createSavedCypherItemInternal = useCallback(
    async (
      savedCypherTreeId: string,
      flatSavedCypherTree: FlatTreeItems,
      payload: CreateSavedCypherFolderPayload | CreateSavedCypherQueryPayload,
    ): Promise<string> => {
      setCreateSavedCypherItemInternalState({ isLoading: true });

      if (flatSavedCypherTree.length >= MAXIMUM_SAVED_CYPHER_ITEMS) {
        const error = new Error(`Maximum number of saved items reached (${MAXIMUM_SAVED_CYPHER_ITEMS}).`);

        setCreateSavedCypherItemInternalState({
          isLoading: false,
        });

        throw error;
      }

      const { parentId } = payload;

      if (storageApiEnabled) {
        const folders = flatSavedCypherTree.filter((item) => isSavedCypherFolder(item));
        const name = isCreateSavedCypherQueryPayload(payload)
          ? payload.name
          : (payload.name ?? getNewFolderName(folders, payload.name));
        const query = isCreateSavedCypherQueryPayload(payload) ? payload.query : null;

        const res = await batchCreateSavedCypherItemsMutation({
          savedCypherTreeId,
          items: [
            {
              name,
              query,
              parentId: parentId ?? ROOT_ITEM_ID,
              clientId: nanoid(),
            },
          ],
        });

        const [item] = res;
        return item?.id ?? '';
      }

      try {
        if (isCreateSavedCypherQueryPayload(payload)) {
          dispatch(setSavedCypherLatestFolderSavedIn(parentId));
          const { id } = await dispatch(createSavedCypherQuery(payload)).unwrap();
          setCreateSavedCypherItemInternalState({ isLoading: false });
          return id;
        }

        const { id } = await dispatch(createSavedCypherFolder(payload)).unwrap();
        setCreateSavedCypherItemInternalState({ isLoading: false });
        return id;
      } catch (error) {
        setCreateSavedCypherItemInternalState({ isLoading: false });
        throw error;
      }
    },
    [storageApiEnabled, batchCreateSavedCypherItemsMutation, dispatch],
  );

  return [createSavedCypherItemInternal, createSavedCypherItemInternalState] as const;
};

export const useUpdateSavedCypherItem = () => {
  const dispatch = useAppDispatch();
  const storageApiEnabled = useStorageApi('query:saved-cypher-shared-storage');
  const [batchUpdateSavedCypherItemsMutation, batchUpdateSavedCypherItemsMutationState] =
    useBatchUpdateSavedCypherItemsMutation();

  const [updateSavedCypherItemInternalState, setUpdateSavedCypherItemInternalState] = useState<
    Pick<SavedCypherLoadingState, 'isLoading'>
  >({
    isLoading: false,
  });

  useEffect(() => {
    if (storageApiEnabled) {
      setUpdateSavedCypherItemInternalState({
        isLoading: batchUpdateSavedCypherItemsMutationState.isLoading,
      });
    }
  }, [storageApiEnabled, batchUpdateSavedCypherItemsMutationState.isLoading]);

  const updateSavedCypherItemInternal = useCallback(
    async (savedCypherTreeId: string, payload: UpdateSavedCypherFolderPayload | UpdateSavedCypherQueryPayload) => {
      setUpdateSavedCypherItemInternalState({ isLoading: true });

      if (storageApiEnabled) {
        const { id: itemId } = payload;
        let name = '';
        let query: string | null = null;

        if (isUpdateSavedCypherQueryPayload(payload)) {
          name = payload.newValues.name ?? '';
          query = payload.newValues.query ?? null;
        } else {
          name = payload.name;
        }

        await batchUpdateSavedCypherItemsMutation({
          savedCypherTreeId,
          items: [
            {
              id: itemId,
              name,
              query,
            },
          ],
        });
      }

      try {
        dispatch(updateSavedCypherItem(payload));
        setUpdateSavedCypherItemInternalState({ isLoading: false });
      } catch (error) {
        setUpdateSavedCypherItemInternalState({ isLoading: false });
        throw error;
      }
    },
    [storageApiEnabled, batchUpdateSavedCypherItemsMutation, dispatch],
  );

  return [updateSavedCypherItemInternal, updateSavedCypherItemInternalState] as const;
};

export const useCreateOrUpdateSavedCypherItem = () => {
  const dispatch = useAppDispatch();
  const storageApiEnabled = useStorageApi('query:saved-cypher-shared-storage');
  const [createItem, createItemState] = useCreateSavedCypherItem();
  const [updateItem, updateItemState] = useUpdateSavedCypherItem();

  const [createOrUpdateSavedCypherItemInternalState, setCreateOrUpdateSavedCypherItemInternalState] = useState<
    Pick<SavedCypherLoadingState, 'isLoading'>
  >({
    isLoading: false,
  });

  useEffect(() => {
    if (storageApiEnabled) {
      setCreateOrUpdateSavedCypherItemInternalState({
        isLoading: createItemState.isLoading || updateItemState.isLoading,
      });
    }
  }, [storageApiEnabled, createItemState.isLoading, updateItemState.isLoading]);

  const createOrUpdateSavedCypherItemInternal = useCallback(
    async (
      savedCypherTreeId: string,
      flatSavedCypherTree: FlatTreeItems,
      name: string,
      parentId: string,
      query: string | null,
    ) => {
      setCreateOrUpdateSavedCypherItemInternalState({
        isLoading: true,
      });

      const existingQuery = findDuplicateInFolder(flatSavedCypherTree, parentId, name, 'items');

      if (existingQuery) {
        trackSavedCypherOverwrite();
        try {
          await updateItem(savedCypherTreeId, {
            id: existingQuery.id,
            newValues: { name, query: query ?? '', parentId },
          });
          dispatch(openSidebarTab({ tabId: 'SAVED_CYPHER', tabButtonPingAnimationEnabled: true }));
          setCreateOrUpdateSavedCypherItemInternalState({
            isLoading: false,
          });

          return existingQuery.id;
        } catch (error) {
          setCreateOrUpdateSavedCypherItemInternalState({ isLoading: false });
          throw error;
        }
      } else {
        try {
          const id = await createItem(savedCypherTreeId, flatSavedCypherTree, {
            name,
            ...(isNotNullish(query) ? { query } : {}),
            parentId,
          });
          dispatch(openSidebarTab({ tabId: 'SAVED_CYPHER', tabButtonPingAnimationEnabled: true }));
          setCreateOrUpdateSavedCypherItemInternalState({ isLoading: false });

          return id;
        } catch (error) {
          setCreateOrUpdateSavedCypherItemInternalState({ isLoading: false });
          throw error;
        }
      }
    },
    [createItem, dispatch, updateItem],
  );

  return [createOrUpdateSavedCypherItemInternal, createOrUpdateSavedCypherItemInternalState] as const;
};

export const useUpdateSavedCypherTree = () => {
  const dispatch = useAppDispatch();
  const storageApiEnabled = useStorageApi('query:saved-cypher-shared-storage');
  const [updateSavedCypherTreeMutation, updateSavedCypherTreeMutationState] = useCommonPatchSavedCypherTreeMutation();

  const [updateSavedCypherTreeInternalState, setUpdateSavedCypherTreeInternalState] = useState<
    Pick<SavedCypherLoadingState, 'isLoading'>
  >({
    isLoading: false,
  });

  useEffect(() => {
    if (storageApiEnabled) {
      setUpdateSavedCypherTreeInternalState({
        isLoading: updateSavedCypherTreeMutationState.isLoading,
      });
    }
  }, [storageApiEnabled, updateSavedCypherTreeMutationState.isLoading]);

  const updateSavedCypherTreeInternal = useCallback(
    async (savedCypherTreeId: string, flatItems: FlatTreeItems) => {
      setUpdateSavedCypherTreeInternalState({ isLoading: true });

      if (storageApiEnabled) {
        const orderedFlatItems = flatItems.map((item, order) => ({ ...item, order }));
        await updateSavedCypherTreeMutation({ id: savedCypherTreeId, flatItems: orderedFlatItems });
      }

      try {
        const orderedFlatItems = flatItems.map((item, order) => ({ ...item, order }));
        dispatch(updateSavedCypherItems(orderedFlatItems));
        setUpdateSavedCypherTreeInternalState({ isLoading: false });
      } catch (error) {
        setUpdateSavedCypherTreeInternalState({ isLoading: false });
        throw error;
      }
    },
    [dispatch, storageApiEnabled, updateSavedCypherTreeMutation],
  );

  return [updateSavedCypherTreeInternal, updateSavedCypherTreeInternalState] as const;
};

export const useImportSavedCypherItems = () => {
  const dispatch = useAppDispatch();
  const storageApiEnabled = useStorageApi('query:saved-cypher-shared-storage');
  const [batchCreateSavedCypherItemsMutation, batchCreateSavedCypherItemsMutationState] =
    useCommonBatchCreateSavedCypherItemsMutation();
  const [batchUpdateSavedCypherItemsMutation, batchUpdateSavedCypherItemsMutationState] =
    useBatchUpdateSavedCypherItemsMutation();

  const [importSavedCypherItemsInternalState, setImportSavedCypherItemsInternalState] = useState<
    Pick<SavedCypherLoadingState, 'isLoading'>
  >({
    isLoading: false,
  });

  useEffect(() => {
    if (storageApiEnabled) {
      setImportSavedCypherItemsInternalState({
        isLoading:
          batchUpdateSavedCypherItemsMutationState.isLoading || batchCreateSavedCypherItemsMutationState.isLoading,
      });
    }
  }, [
    storageApiEnabled,
    batchUpdateSavedCypherItemsMutationState.isLoading,
    batchCreateSavedCypherItemsMutationState.isLoading,
  ]);

  const importSavedCypherItemsInternal = useCallback(
    async (savedCypherTreeId: string, savedCypherTreeItems: FlatTreeItems, newSavedCypherTreeItems: FlatTreeItems) => {
      setImportSavedCypherItemsInternalState({ isLoading: true });

      if (
        savedCypherTreeItems.length + newSavedCypherTreeItems.filter((item) => item.isNew).length >
        MAXIMUM_SAVED_CYPHER_ITEMS
      ) {
        const error = new Error(`Maximum number of saved Cypher items reached (${MAXIMUM_SAVED_CYPHER_ITEMS}).`);
        setImportSavedCypherItemsInternalState({ isLoading: false });
        throw error;
      }

      if (storageApiEnabled) {
        const newItems = newSavedCypherTreeItems
          .filter((item) => item.isNew)
          .map((item) => {
            return isSavedCypherQuery(item)
              ? { ...item, query: item.query, clientId: item.id, parentId: item.parentId ?? ROOT_ITEM_ID }
              : { ...item, query: null, clientId: item.id, parentId: item.parentId ?? ROOT_ITEM_ID };
          });

        const existingItems = newSavedCypherTreeItems
          .filter((item) => item.isNew === false || !('isNew' in item))
          .map((item) => {
            return isSavedCypherQuery(item)
              ? { ...item, query: item.query, parentId: item.parentId ?? ROOT_ITEM_ID }
              : { ...item, query: null, parentId: item.parentId ?? ROOT_ITEM_ID };
          });

        if (newItems.length) {
          await batchCreateSavedCypherItemsMutation({
            savedCypherTreeId,
            items: newItems,
          });
        }

        if (existingItems.length) {
          await batchUpdateSavedCypherItemsMutation({
            savedCypherTreeId,
            items: existingItems,
          });
        }
      } else {
        try {
          dispatch(importSavedCypherItems(newSavedCypherTreeItems));
        } finally {
          setImportSavedCypherItemsInternalState({ isLoading: false });
        }
      }
    },
    [storageApiEnabled, batchCreateSavedCypherItemsMutation, batchUpdateSavedCypherItemsMutation, dispatch],
  );

  return [importSavedCypherItemsInternal, importSavedCypherItemsInternalState] as const;
};

/**
 * Retrieves the user's saved Cypher scripts and folders (Favourites) from Browser,
 * mapping them to an array of flat tree items for importing into Query.
 * @param datestamp - The date and time of the import.
 * @return A flat tree of saved Cypher items, including the parent folder to import to, or null if no items are found.
 */
const importFavouritesFromBrowser = (datestamp: string): FlatTreeItems | null => {
  const favouritesMigrated = getItemFromLocalStorage<boolean>('neo4j.favouritesMigrated', false);

  if (favouritesMigrated) {
    return null;
  }

  const id = nanoid();

  const folders: FlatSavedCypherFolder[] = getItemFromLocalStorage<ImportedBrowserFolder[]>('neo4j.folders', [])
    .filter((browserFolder) => browserFolder.isStatic === undefined || !browserFolder.isStatic)
    .map((browserFolder, index) => ({
      id: browserFolder.id,
      name: browserFolder.name,
      parentId: id,
      depth: 1,
      order: index,
      children: [],
    }));

  const documents: FlatSavedCypherQuery[] = getItemFromLocalStorage<ImportedBrowserDocument[]>('neo4j.documents', [])
    .filter((document) => document.isStatic === undefined || !document.isStatic)
    .map((browserDocument, index) => ({
      id: browserDocument.id,
      name: getNameForSavedCypher(browserDocument.content),
      parentId: browserDocument.folder ?? id,
      depth: browserDocument.folder !== undefined ? 2 : 1,
      order: index,
      query: browserDocument.content,
    }));

  const flatTree = [...folders, ...documents];

  if (flatTree.length === 0) {
    return null;
  }

  const folder: FlatSavedCypherFolder = {
    id,
    order: 0,
    depth: 0,
    parentId: ROOT_ITEM_ID,
    name: `Imported from Browser ${datestamp}`,
    children: flatTree,
  };

  return [folder, ...flatTree];
};

/**
 * Imports the user's saved Cypher scripts and folders (Favourites) from Browser into localstorage.
 *
 * @param datestamp - The date and time of the import.
 * @param flatTreeItems - The current flat tree items in localstorage.
 * @returns An array of flat tree items merged with Favourites from Browser.
 */
const useImportAndMergeBrowserFavouritesWithLocalStorageTreeItems = () => {
  const dispatch = useAppDispatch();

  return (datestamp: string, flatTreeItems: FlatTreeItems): FlatTreeItems => {
    const flatTreeItemsFromBrowser = importFavouritesFromBrowser(datestamp);
    if (flatTreeItemsFromBrowser === null) {
      return flatTreeItems;
    }

    if (flatTreeItems.length + flatTreeItemsFromBrowser.length > MAXIMUM_SAVED_CYPHER_ITEMS) {
      Sentry.captureException(
        new Error(`Maximum number of saved Cypher items reached (${MAXIMUM_SAVED_CYPHER_ITEMS}).`),
      );

      return [];
    }

    dispatch(importSavedCypherItems(flatTreeItemsFromBrowser));
    setItemInLocalStorage('neo4j.favouritesMigrated', true);
    return [...flatTreeItems, ...flatTreeItemsFromBrowser];
  };
};

/**
 * Migrates the user's saved Cypher from localstorage to shared storage.,
 * then sets a flag in local storage to indicate that the migration has been performed.
 * @param datestamp - The date and time of the migration.
 */
const useMigrateLocalStorageTreeItemsToSharedStorage = () => {
  const dispatch = useAppDispatch();
  const savedCypherTreeMigratedV2 = useAppSelector(selectSavedCypherTreeMigrated);
  const storageApiEnabled = useStorageApi('query:saved-cypher-shared-storage');
  const [batchCreateSavedCypherItemsMutation] = useCommonBatchCreateSavedCypherItemsMutation();

  return async (
    datestamp: string,
    savedCypherTreeId: string,
    flatSavedCypherTree: FlatTreeItems,
    localStorageFlatSavedCypherTree: FlatTreeItems,
  ) => {
    if (!storageApiEnabled || savedCypherTreeMigratedV2 || localStorageFlatSavedCypherTree.length === 0) {
      return;
    }

    const folderId = nanoid();
    const folder = {
      id: folderId,
      clientId: folderId,
      parentId: ROOT_ITEM_ID,
      name: `Migrated from web browser cache ${datestamp}`,
      query: null,
      children: [],
    };

    const newTreeItems = [
      folder,
      ...localStorageFlatSavedCypherTree.map((item) => {
        const query = isCreateSavedCypherQueryPayload(item) ? item.query : null;
        return {
          id: item.id,
          clientId: item.id,
          parentId: !isChild(item) ? folder.id : item.parentId,
          name: item.name,
          query,
        };
      }),
    ];

    if (flatSavedCypherTree.length + newTreeItems.length > MAXIMUM_SAVED_CYPHER_ITEMS) {
      Sentry.captureException(
        new Error(`Maximum number of saved Cypher items reached (${MAXIMUM_SAVED_CYPHER_ITEMS}).`),
      );
    } else {
      await batchCreateSavedCypherItemsMutation({
        savedCypherTreeId,
        items: newTreeItems,
      });

      dispatch(setSavedCypherTreeMigrated(true));
    }
  };
};

/**
 * Imports the user's Favourites from Browser, merging them with the local storage tree items.
 * Then attempts to migrate the local storage tree items to shared storage, if enabled.
 */
export const useImportAndMigrateSavedCypherTrees = () => {
  const localStorageFlatTreeItems = useAppSelector(selectFlatSavedCypherTree);
  const importAndMergeBrowserFavouritesWithLocalStorageTreeItems =
    useImportAndMergeBrowserFavouritesWithLocalStorageTreeItems();
  const migrateLocalStorageTreeItemsToSharedStorage = useMigrateLocalStorageTreeItemsToSharedStorage();

  const [importAndMigrateSavedCypherTreesInternalState, setImportAndMigrateSavedCypherTreesInternalState] = useState<
    Pick<SavedCypherLoadingState, 'isLoading'>
  >({
    isLoading: false,
  });

  const importAndMigrateSavedCypherTreesInternal = useCallback(
    async (savedCypherTreeId: string, savedCypherTreeItems: FlatTreeItems) => {
      const datestamp = new Intl.DateTimeFormat('en-US', {
        hour12: false,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
      }).format(new Date());

      setImportAndMigrateSavedCypherTreesInternalState({ isLoading: true });

      try {
        const mergedLocalStorageTreeItems = importAndMergeBrowserFavouritesWithLocalStorageTreeItems(
          datestamp,
          localStorageFlatTreeItems,
        );
        await migrateLocalStorageTreeItemsToSharedStorage(
          datestamp,
          savedCypherTreeId,
          savedCypherTreeItems,
          mergedLocalStorageTreeItems,
        );
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        setImportAndMigrateSavedCypherTreesInternalState({ isLoading: false });
      }
    },
    [
      localStorageFlatTreeItems,
      importAndMergeBrowserFavouritesWithLocalStorageTreeItems,
      migrateLocalStorageTreeItemsToSharedStorage,
    ],
  );

  return [importAndMigrateSavedCypherTreesInternal, importAndMigrateSavedCypherTreesInternalState] as const;
};

export default savedCypher.reducer;
